import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Switch,
  FormControlLabel,
  TextareaAutosize,
} from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __updateMmsTmobilej } from "../../../../api/sim";

const TmobilejMmsForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [enableMms, setEnableMms] = useState(true);
  const { setAlertMessage } = useAlert();
  const initialData = { lines: [] };
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__updateMmsTmobilej,
  });

  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    msisdn: yup
      .string("msisdn")
      .min(10, "Should be exactly 10 digits")
      .max(10, "Should be exactly 10 digits")
      .matches(/^\d+$/, "Must be digits only")
      .required(),
  });

  useEffect(() => {
    if (formData?.lines?.length) {
      call({
        data: { lines: formData.lines, enable_mms: enableMms },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "MMS update queue started",
      });
      setLinesData([]);
      nav("/tmobilej/mms");
    }
    formik.setSubmitting(false);
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return false;

      const parts = trimmed.split(",");
      const msisdn = (parts[0] || "").trim().replace(/^\+1/, "");
      if (msisdn.length !== 10 || !/^\d+$/.test(msisdn)) {
        errors.lines = `Invalid MSISDN in line ${idx + 1}: ${line}`;
        return true;
      }

      try {
        lineValidationSchema.validateSync({ msisdn });
        formatedLines.push({ msisdn });
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: () => {
      const parsed = [];
      const lines = rawData.split("\n");
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue;
        const msisdn = trimmed.split(",")[0].trim().replace(/^\+1/, "");
        if (msisdn.length !== 10 || !/^\d+$/.test(msisdn)) continue;
        parsed.push({ msisdn });
      }
      if (parsed.length) {
        setFormData({ lines: parsed });
      }
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();
    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };

  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];
    if (e.target.files.length) {
      const inputFile = e.target.files[0];
      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors,
          upload: "Please input a csv file",
        });
        return;
      }
      handleParse(inputFile);
    }
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title variant="h5" content="MMS" sx={{ mb: 3 }} gutterBottom />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl sx={{ width: "100%" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableMms}
                    onChange={(e) => setEnableMms(e.target.checked)}
                  />
                }
                label={enableMms ? "Enable MMS" : "Disable MMS"}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) && (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              Add phone numbers (10-digit MSISDN, one per line)
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines)}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder="1234567890\n9876543210"
                style={{ minWidth: 550 }}
                minRows={5}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) && (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={formik.errors.lines}
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {enableMms ? "Enable MMS" : "Disable MMS"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejMmsForm;
